import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Repeat, Gift, Copy, LogOut, Settings, Loader2, CalendarClock, KeyRound, FileText } from "lucide-react";
import api, { externalRedirect } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const nav = useNavigate();
  const { user, loading, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [subs, setSubs] = useState([]);
  const [referral, setReferral] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [rsId, setRsId] = useState(null);
  const [rslots, setRslots] = useState([]);
  const [rDate, setRDate] = useState("");
  const [rTime, setRTime] = useState("");
  const [rBusy, setRBusy] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (!loading && !user) nav("/login?next=/account");
  }, [loading, user, nav]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [b, s, r] = await Promise.all([
          api.get("/account/bookings"),
          api.get("/account/subscriptions"),
          api.get("/account/referral"),
        ]);
        setBookings(b.data.bookings);
        setSubs(s.data.subscriptions);
        setReferral(r.data);
        try { const iv = await api.get("/account/invoices"); setInvoices(iv.data.invoices); } catch { /* non-critical */ }
      } catch { toast.error("Couldn't load your account. Please refresh."); }
    })();
  }, [user]);

  const referralUrl = referral ? `${window.location.origin}/booking?ref=${referral.code}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(referralUrl);
    toast.success("Referral link copied!");
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/account/portal", { origin_url: window.location.origin });
      if (data.available && data.portal_url) externalRedirect(data.portal_url);
      else toast.info(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No subscription found.");
    } finally {
      setPortalBusy(false);
    }
  };

  const loadBookings = async () => {
    const b = await api.get("/account/bookings");
    setBookings(b.data.bookings);
  };
  const openReschedule = async (b) => {
    setRsId(b.id); setRDate(""); setRTime("");
    try { const { data } = await api.get("/slots"); setRslots(data.slots || []); }
    catch { toast.error("Couldn't load available times. Please try again."); }
  };
  const doReschedule = async () => {
    if (!rDate || !rTime) return;
    setRBusy(true);
    try {
      const { data } = await api.post(`/account/bookings/${rsId}/reschedule`, { slot_date: rDate, slot_time: rTime, origin_url: window.location.origin });
      if (data.payment_required && data.checkout_url) { externalRedirect(data.checkout_url); return; }
      toast.success("Visit rescheduled!");
      setRsId(null);
      await loadBookings();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reschedule."); }
    finally { setRBusy(false); }
  };
  const rTimes = rslots.find((s) => s.date === rDate)?.times || [];

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    if (pwForm.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      setPwForm({ current: "", next: "", confirm: "" });
      toast.success("Password changed successfully");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not change password"); }
    finally { setPwBusy(false); }
  };

  if (!user) return null;

  return (
    <div data-testid="account-page" className="mx-auto max-w-4xl px-5 py-14 lg:px-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Hi, {user.name?.split(" ")[0] || "there"} 👋</h1>
          <p className="mt-1 text-muted-foreground">{user.email}</p>
        </div>
        <button data-testid="account-logout" onClick={() => { logout(); nav("/"); }}
          className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-bold hover:bg-accent">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </div>

      {/* Referral */}
      <div className="mt-8 rounded-2xl border border-primary bg-accent p-6">
        <div className="flex items-center gap-2 text-accent-foreground">
          <Gift className="h-5 w-5" />
          <h2 className="font-heading text-lg font-bold">Refer a neighbor, earn a free visit</h2>
        </div>
        <p className="mt-2 text-sm text-accent-foreground/80">
          When a neighbor books their first cleaning with your link, you get a free visit. You've referred{" "}
          <strong>{referral?.referred_count ?? 0}</strong> · Free-visit credits: <strong>{referral?.credits ?? 0}</strong>
        </p>
        <div className="mt-4 flex gap-2">
          <input data-testid="referral-link" readOnly value={referralUrl}
            className="flex-1 rounded-xl border border-border bg-card px-4 py-2.5 text-sm" />
          <button data-testid="referral-copy" onClick={copyLink}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:brightness-110">
            <Copy className="h-4 w-4" /> Copy
          </button>
        </div>
      </div>

      {/* Subscriptions */}
      <div className="mt-8">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold"><Repeat className="h-5 w-5 text-primary" /> My Subscription</h2>
        {subs.length === 0 ? (
          <p className="mt-3 text-muted-foreground">No active subscription. <Link to="/booking" className="font-semibold text-primary">Start a plan →</Link></p>
        ) : (
          <div className="mt-4 space-y-3">
            {subs.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5">
                <div>
                  <div className="font-heading font-bold">{s.plan_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {s.bills_count}/{s.min_bills} visit minimum · <span className={s.status === "active" ? "text-primary font-semibold" : ""}>{s.status}</span>
                  </div>
                </div>
                <button data-testid="account-manage-sub" onClick={openPortal} disabled={portalBusy}
                  className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-bold hover:bg-accent disabled:opacity-50">
                  {portalBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />} Manage / Pause / Cancel
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bookings */}
      <div className="mt-8">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold"><CalendarDays className="h-5 w-5 text-primary" /> My Bookings</h2>
        {rsId && (
          <div data-testid="reschedule-panel" className="mt-4 rounded-2xl border border-primary bg-accent/40 p-5">
            <h3 className="font-heading font-bold">Reschedule your visit</h3>
            <p className="mt-1 text-sm text-muted-foreground">Please make sure your bins are empty (no trash bags) on your new day.</p>
            {(() => { const bk = bookings.find((x) => x.id === rsId); const used = bk?.reschedule_count || 0; const waived = (bk?.free_reschedule_credits || 0) > 0; return (
              <p data-testid="reschedule-fee-note" className={`mt-1 text-sm font-semibold ${used >= 3 && !waived ? "text-destructive" : "text-accent-foreground"}`}>
                {used >= 3 && !waived ? "You've used your 3 free reschedules — a $10 fee applies (secure card payment)." : `Free reschedules used: ${used} of 3.${waived ? " · Fee waived by owner — this one's free!" : ""}`}
              </p>
            ); })()}
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <select data-testid="reschedule-date" value={rDate} onChange={(e) => { setRDate(e.target.value); setRTime(""); }}
                className="rounded-xl border border-border bg-card px-4 py-2.5">
                <option value="">Pick a day</option>
                {rslots.map((s) => <option key={s.date} value={s.date}>{s.display}</option>)}
              </select>
              <select data-testid="reschedule-time" value={rTime} onChange={(e) => setRTime(e.target.value)} disabled={!rDate}
                className="rounded-xl border border-border bg-card px-4 py-2.5 disabled:opacity-50">
                <option value="">Pick a time</option>
                {rTimes.filter((t) => t.available > 0).map((t) => <option key={t.time} value={t.time}>{t.label} — {t.available} left</option>)}
              </select>
            </div>
            <div className="mt-3 flex gap-2">
              <button data-testid="reschedule-confirm" onClick={doReschedule} disabled={rBusy || !rDate || !rTime}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50">
                {rBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm new time"}
              </button>
              <button onClick={() => setRsId(null)} className="rounded-xl border border-border px-5 py-2.5 text-sm font-bold">Cancel</button>
            </div>
          </div>
        )}
        {bookings.length === 0 ? (
          <p className="mt-3 text-muted-foreground">No bookings yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-border bg-muted/40">
                {["Plan", "Date", "Window", "Bins", "Status", "Actions"].map((h) => <th key={h} className="px-4 py-3 font-semibold text-muted-foreground">{h}</th>)}
              </tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-border/50" data-testid={`account-booking-${b.id}`}>
                    <td className="px-4 py-3">{b.plan_name}</td>
                    <td className="px-4 py-3">{b.slot_date}</td>
                    <td className="px-4 py-3">{b.slot_label}</td>
                    <td className="px-4 py-3">{b.num_bins}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-bold ${b.status === "needs_reschedule" ? "bg-destructive/15 text-destructive" : "bg-accent text-accent-foreground"}`}>{b.status === "needs_reschedule" ? "reschedule needed" : b.status}</span></td>
                    <td className="px-4 py-3">{["confirmed", "needs_reschedule"].includes(b.status) && <button data-testid={`reschedule-${b.id}`} onClick={() => openReschedule(b)} className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-bold hover:bg-accent"><CalendarClock className="h-4 w-4" /> Reschedule</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoices */}
      {invoices.length > 0 && (
        <div className="mt-8">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold"><FileText className="h-5 w-5 text-primary" /> Invoices & Receipts</h2>
          <div className="mt-4 space-y-3">
            {invoices.map((iv) => (
              <div key={iv.id} data-testid={`account-invoice-${iv.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4">
                <div>
                  <p className="font-semibold">Invoice {iv.number || ""} · ${(iv.total || 0).toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">{(iv.created_at || "").slice(0, 10)} · <span className={iv.status === "paid" ? "text-secondary" : "text-primary"}>{iv.status}</span></p>
                </div>
                <div className="flex gap-2">
                  {iv.status !== "paid" && iv.hosted_invoice_url && <a href={iv.hosted_invoice_url} target="_blank" rel="noreferrer" data-testid={`pay-invoice-${iv.id}`} className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:brightness-110">Pay Now</a>}
                  {iv.invoice_pdf && <a href={iv.invoice_pdf} target="_blank" rel="noreferrer" className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent/40">PDF</a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Change password */}
      <div className="mt-8">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold"><KeyRound className="h-5 w-5 text-primary" /> Change Password</h2>
        <form onSubmit={changePassword} data-testid="account-change-password" className="mt-4 max-w-md space-y-3 rounded-2xl border border-border bg-card p-6">
          <input data-testid="account-pw-current" type="password" required placeholder="Current password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
            className="w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
          <input data-testid="account-pw-new" type="password" required placeholder="New password (min 8 chars)" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
            className="w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
          <input data-testid="account-pw-confirm" type="password" required placeholder="Confirm new password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
            className="w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
          <button data-testid="account-pw-submit" type="submit" disabled={pwBusy}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 font-bold text-primary-foreground hover:brightness-110 disabled:opacity-50">
            {pwBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
